"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale } from "next-intl";
import { useParams } from "next/navigation";
import { useTransition } from "react";
import { Check, ChevronDown, Globe } from "lucide-react";
import { usePathname, useRouter } from "@/i18n/navigation";
import { routing, type Locale } from "@/i18n/routing";
import { cn } from "@/lib/utils";

/**
 * Le nom de chaque langue, DANS cette langue.
 *
 * Un lusophone cherche « Português », pas « Portugais » : traduire les noms de
 * langues dans la langue courante ne sert que ceux qui la lisent déjà, or ce
 * menu s'adresse justement à ceux qui ne la lisent pas. C'est pour la même
 * raison qu'ils ne passent pas par les fichiers de traduction.
 *
 * Une langue absente d'ici retombe sur son code en majuscules : ajouter une
 * locale à `routing` ne peut donc pas casser l'affichage, seulement le rendre
 * moins joli le temps d'écrire son nom.
 */
const NOMS: Partial<Record<Locale, string>> = {
  fr: "Français",
  en: "English",
};

const nomDe = (code: Locale) => NOMS[code] ?? code.toUpperCase();

/**
 * Sélecteur de langue — une liste, pas une bascule.
 *
 * La pastille « FR EN » d'avant ne marchait qu'à deux : elle affichait la
 * langue courante ET la seule autre possible. Avec un troisième marché, il n'y
 * a plus de « l'autre ». Cette liste tient le nombre qu'on voudra ; ajouter
 * une langue, c'est l'ajouter à `routing.locales` et lui écrire un nom.
 */
export function LanguageToggle() {
  const locale = useLocale() as Locale;
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const [, startTransition] = useTransition();
  const [ouvert, setOuvert] = useState(false);
  const boite = useRef<HTMLDivElement>(null);

  // Refermer au clic dehors et à Échap. Sans ça, le menu reste ouvert
  // derrière le contenu dès qu'on clique ailleurs — et il est en `z-50`,
  // donc il masquerait la page.
  useEffect(() => {
    if (!ouvert) return;
    const auClic = (e: MouseEvent) => {
      if (!boite.current?.contains(e.target as Node)) setOuvert(false);
    };
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOuvert(false);
    };
    document.addEventListener("mousedown", auClic);
    document.addEventListener("keydown", auClavier);
    return () => {
      document.removeEventListener("mousedown", auClic);
      document.removeEventListener("keydown", auClavier);
    };
  }, [ouvert]);

  const basculer = (cible: Locale) => {
    setOuvert(false);
    if (cible === locale) return;
    // La requête suit le changement de langue : sans elle, le formulaire de
    // prospection perdait `?agent=` et la fiche n'allait plus au bon agent.
    // Lue au clic plutôt que par useSearchParams, qui obligerait chaque page
    // statique à se rendre côté client.
    const query = Object.fromEntries(new URLSearchParams(window.location.search));
    startTransition(() => {
      router.replace(
        // @ts-expect-error -- params dynamiques transmis tels quels
        { pathname, params, query },
        { locale: cible },
      );
    });
  };

  return (
    <div ref={boite} className="relative">
      <button
        onClick={() => setOuvert((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={ouvert}
        aria-label={nomDe(locale)}
        className="flex cursor-pointer items-center gap-1.5 rounded-full border border-[#DDD1C2] bg-white py-[7px] pl-2.5 pr-2 text-[13px] font-semibold text-espresso transition hover:border-[#CBBBA6]"
      >
        <Globe size={14} className="text-ink-faint" aria-hidden />
        {locale.toUpperCase()}
        <ChevronDown
          size={13}
          aria-hidden
          className={cn("text-ink-faint transition-transform", ouvert && "rotate-180")}
        />
      </button>

      {ouvert && (
        <ul
          role="listbox"
          aria-label={nomDe(locale)}
          className="absolute right-0 top-[calc(100%+8px)] z-50 min-w-[168px] overflow-hidden rounded-[14px] border border-line bg-white py-1.5 shadow-[0_20px_44px_-18px_rgba(28,16,10,.35)]"
        >
          {routing.locales.map((code) => {
            const actif = code === locale;
            return (
              <li key={code}>
                <button
                  role="option"
                  aria-selected={actif}
                  onClick={() => basculer(code)}
                  className={cn(
                    "flex w-full cursor-pointer items-center justify-between gap-4 px-3.5 py-2 text-left text-[14px] transition hover:bg-cream",
                    actif ? "font-bold text-espresso" : "font-medium text-ink",
                  )}
                >
                  {nomDe(code)}
                  {actif ? (
                    <Check size={14} className="text-terracotta" aria-hidden />
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
